import Card from 'Components/DesignSystem/Card/Card';
import TextField from 'Components/DesignSystem/TextField/TextField';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Toggle from 'Components/DesignSystem/Toggle/Toggle';
import Button from 'Components/DesignSystem/Button/Button';
import Tooltip from 'Components/DesignSystem/Tooltip/Tooltip';
import Typography from 'Components/DesignSystem/Typography/Typography';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import {
    CLASS,
    CUSTOM_LABEL,
    ENTER,
    ENTER_LABEL,
    LABEL,
    MANAGE,
    MAX,
    MIN,
    MULTIPLE_INSTANTIATION,
    PROJECT_NAME,
    QUANTITY,
    REQUIRED,
    SUB_MODEL_DETAILS,
    SUB_MODEL_NAME
} from 'Pages/GMS/ISR/ISRConstants';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { AppContext } from 'Context/AppContext';
import { TConnector, TField, TFieldClass, TIndexState, TProductCategory, TSubmodel, TUpsertISRProjectDataModelsInput } from '../model';
import { ClassIcon, GearIcon } from 'assests/GmsIcons';
import ManageClassDialog from './ManageClassDialog';
import { useISRContext } from 'Pages/GMS/ISR/Hooks/useISRContext';
import { getAllProjectClass } from '../ModelServices/ModalServices';

const GUIDE_LOCKED_MESSAGE = 'Available with Configure';

type SubModelDetailsProps = {
    id: string;
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    setUserProjectDataModels: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
    indexInfo: TIndexState;
    projectId?: string;
    hasFreestyleUIModel?: boolean;
}

const SubModelDetails = ({ id, selectedItem, setUserProjectDataModels, indexInfo, projectId, hasFreestyleUIModel = false }: SubModelDetailsProps) => {
    const initializeClass = { id: null, name: '', type: '', colour: '' };
    const [submodelInfo, setSubmodelInfo] = useState<TSubmodel>({
        type: "Submodel",
        data: { name: '', label: '', class: initializeClass, canSetCustomLabel: false, minInstantiation: 1, maxInstantiation: 1, fields: [], productCategories: [], connectors: [], required: false },
    });
    const isrContext = useISRContext();
    const location = useLocation();
    const isGuide = location.pathname.includes('/guide');
    const { enableConfigure } = useContext(AppContext);
    const isConfigureDisabled = isGuide && !enableConfigure;
    const [instanceRangeError, setInstanceRangeError] = useState<string>('');
    const [quantityRangeError, setQuantityRangeError] = useState<string>('');
    const [showFreestyleMinInstTooltip, setShowFreestyleMinInstTooltip] = useState<boolean>(false);
    const [showFreestyleMaxInstTooltip, setShowFreestyleMaxInstTooltip] = useState<boolean>(false);
    const [showConfigureLockedMinTooltip, setShowConfigureLockedMinTooltip] = useState<boolean>(false);
    const [showConfigureLockedMaxTooltip, setShowConfigureLockedMaxTooltip] = useState<boolean>(false);
    const [showSelectClassTooltip, setShowSelectClassTooltip] = useState<boolean>(false);
    const [showManageTooltip, setShowManageTooltip] = useState<boolean>(false);

    const [showClassMenu, setShowClassMenu] = useState<boolean>(false);
    // Tracks whether submodelInfo's current value originated from a user edit
    // (vs being synced from selectedItem). Only user edits should trigger
    // a write-back to userProjectDataModels.
    const userEditedRef = useRef(false);

    const openConnectorMenu = () => setShowClassMenu(true);

    useEffect(() => {
        userEditedRef.current = false;
        const newSubmodel = selectedItem as TSubmodel;
        setSubmodelInfo(newSubmodel);
        const data = newSubmodel?.data;
        if (data) {
            const minInst = data.minInstantiation ?? 1;
            const maxInst = data.maxInstantiation ?? 1;
            setInstanceRangeError(minInst > maxInst ? 'Minimum value cannot be greater than the maximum.' : '');
            const minQ = data.min ?? 0;
            const maxQ = data.max ?? 0;
            setQuantityRangeError(minQ > maxQ ? 'Minimum value cannot be greater than the maximum.' : '');
        }
    }, [selectedItem]);

    useEffect(() => {
        if (!userEditedRef.current) return;
        if (instanceRangeError || quantityRangeError) return;
        updateSubmodelInDataModels(submodelInfo);
    }, [submodelInfo, instanceRangeError, quantityRangeError]);

    const handleLabelChange = (value: string) => {
        userEditedRef.current = true;
        setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, label: value, }, }));
    };

    const handleProjectNameChange = (value: string) => {
        userEditedRef.current = true;
        setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, name: value, }, }));
    };

    const handleCustomLabelChange = (value: boolean) => {
        userEditedRef.current = true;
        setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, canSetCustomLabel: value, }, }));
    };

    const handleMinInstancesChange = (value: number) => {
        const clampedValue = Math.max(0, value);
        userEditedRef.current = true;
        if (isConfigureDisabled && clampedValue > 1) {
            setShowConfigureLockedMinTooltip(true);
            setTimeout(() => setShowConfigureLockedMinTooltip(false), 3000);
            return;
        }
        if (hasFreestyleUIModel && clampedValue > 1) {
            setShowFreestyleMinInstTooltip(true);
            setTimeout(() => setShowFreestyleMinInstTooltip(false), 3000);
            return;
        }
        const currentMax = submodelInfo?.data?.maxInstantiation ?? 1;
        if (clampedValue > currentMax) {
            setInstanceRangeError('Minimum value cannot be greater than the maximum.');
        } else {
            setInstanceRangeError('');
        }
        setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, minInstantiation: clampedValue } }));
    };

    const handleMinInstancesBlur = () => {
        if (isConfigureDisabled && (submodelInfo?.data?.minInstantiation ?? 1) > 1) {
            userEditedRef.current = true;
            setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, minInstantiation: 1 } }));
            return;
        }
        if (hasFreestyleUIModel && (submodelInfo?.data?.minInstantiation ?? 1) > 1) {
            userEditedRef.current = true;
            setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, minInstantiation: 1 } }));
        }
    };

    const handleMaxInstancesChange = (value: number) => {
        const clampedValue = Math.max(0, value);
        userEditedRef.current = true;
        if (isConfigureDisabled && clampedValue > 1) {
            setShowConfigureLockedMaxTooltip(true);
            setTimeout(() => setShowConfigureLockedMaxTooltip(false), 3000);
            return;
        }
        if (hasFreestyleUIModel && clampedValue > 1) {
            setShowFreestyleMaxInstTooltip(true);
            setTimeout(() => setShowFreestyleMaxInstTooltip(false), 3000);
            return;
        }
        const currentMin = submodelInfo?.data?.minInstantiation ?? 1;
        if (currentMin > clampedValue) {
            setInstanceRangeError('Minimum value cannot be greater than the maximum.');
        } else {
            setInstanceRangeError('');
        }
        setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, maxInstantiation: clampedValue } }));
    };

    const handleMaxInstancesBlur = () => {
        if (hasFreestyleUIModel && (submodelInfo?.data?.maxInstantiation ?? 1) > 1) {
            userEditedRef.current = true;
            setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, maxInstantiation: 1 } }));
        }
        if (isConfigureDisabled && (submodelInfo?.data?.maxInstantiation ?? 1) > 1) {
            userEditedRef.current = true;
            setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, maxInstantiation: 1 } }));
        }
    };

    const handleMinChange = (value: number) => {
        const clampedValue = Math.max(0, value);
        userEditedRef.current = true;
        const currentMax = submodelInfo?.data?.max ?? 0;
        if (clampedValue > currentMax) {
            setQuantityRangeError('Minimum value cannot be greater than the maximum.');
        } else {
            setQuantityRangeError('');
        }
        setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, min: clampedValue } }));
    };

    const handleMaxChange = (value: number) => {
        const clampedValue = Math.max(0, value);
        userEditedRef.current = true;
        const currentMin = submodelInfo?.data?.min ?? 0;
        if (currentMin > clampedValue) {
            setQuantityRangeError('Minimum value cannot be greater than the maximum.');
        } else {
            setQuantityRangeError('');
        }
        setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, max: clampedValue } }));
    };

    const updateSubmodelInDataModels = (updatedSubmodel: TSubmodel) => {
        setUserProjectDataModels((prevDataModels) => {
            if (
                !indexInfo ||
                indexInfo.dataModelIndex === null ||
                indexInfo.dataModelIndex < 0 ||
                indexInfo.dataModelIndex >= prevDataModels.project.dataModels.length
            ) {
                return prevDataModels;
            }
            const updatedDataModels = prevDataModels.project.dataModels.map((sm, i) => {
                if (i !== indexInfo.dataModelIndex) return sm;
                return { ...sm, data: { ...sm.data, ...updatedSubmodel.data } };
            });
            return {
                ...prevDataModels,
                project: { ...prevDataModels.project, dataModels: updatedDataModels },
            };
        });
    };

    const handleClassChange = (selectedValue: string | number) => {
        const selectedObject: TFieldClass = isrContext?.modelClasses?.find((item: TFieldClass) => item.name === selectedValue);
        userEditedRef.current = true;
        setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, class: selectedObject, }, }));
    };

    const handleRequiredChange = (value: boolean) => {
        userEditedRef.current = true;
        setSubmodelInfo((prevConfig) => ({ ...prevConfig, data: { ...prevConfig.data, required: value, }, }));
    };

    useEffect(() => {
        fetchAllClasses();
    }, []);

    const fetchAllClasses = async () => {
        try {
            const responseData = await getAllProjectClass(projectId ?? '');
            if (responseData?.done) {
                isrContext?.setFieldClasses(responseData?.data?.fieldClasses ?? []);
                isrContext?.setModelClasses(responseData?.data?.modelClasses ?? []);
            } else {
                //toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        }
    }

    const classOptions = [
        { value: '', label: 'Select Class' },
        ...(isrContext?.modelClasses ?? []).map((item: TFieldClass) => ({
            value: item?.name,
            label: item?.name,
        })),
    ];

    const classDropdown = (
        <Dropdown
            id={`${id}-class-select`}
            data-testid={`${id}-class-select`}
            label=""
            aria-label={CLASS}
            options={classOptions}
            value={submodelInfo?.data?.class?.name ?? ''}
            onChange={handleClassChange}
            disabled={isConfigureDisabled}
            usePortal
            className='tw-w-full'
        />
    );

    const manageClassButton = (
        <Button
            id={`${id}-manage-class-btn`}
            data-testid={`${id}-manage-class-btn`}
            variant="tertiary"
            size="sm"
            icon={<GearIcon />}
            showIcon
            disabled={isConfigureDisabled}
            onClick={isConfigureDisabled ? undefined : openConnectorMenu}
        >
            {MANAGE}
        </Button>
    );

    return (
        <Card id={id} data-testid={id} title={SUB_MODEL_DETAILS}>
            {/* Name and custom-label */}
            <div id={`${id}-name-section`} className='tw-flex tw-items-start tw-gap-4'>
                <TextField
                    id={`${id}-name-input`}
                    data-testid={`${id}-name-input`}
                    label={SUB_MODEL_NAME}
                    value={submodelInfo?.data?.name ?? ''}
                    onChange={handleProjectNameChange}
                    placeholder={PROJECT_NAME}
                    className='tw-w-[60%]'
                />
                <div id={`${id}-custom-label-container`} className='tw-w-[40%]'>
                    <Tooltip content={GUIDE_LOCKED_MESSAGE} disabled={!isConfigureDisabled} placement="top">
                        <span>
                            <Toggle
                                id={`${id}-custom-label-toggle`}
                                data-testid={`${id}-custom-label-toggle`}
                                label={CUSTOM_LABEL}
                                labelPlacement="top"
                                checked={submodelInfo?.data?.canSetCustomLabel ?? false}
                                onChange={handleCustomLabelChange}
                                disabled={isConfigureDisabled}
                            />
                        </span>
                    </Tooltip>
                </div>
            </div>

            {/* Instance */}
            <Typography
                variant="sub-title-label"
                as="p"
                id={`${id}-instantiation-title`}
                className='tw-text-ink-secondary tw-mt-5'
            >
                {MULTIPLE_INSTANTIATION}
            </Typography>
            <div id={`${id}-instantiation-section`} className='tw-flex tw-items-start tw-gap-5 tw-mt-2'>
                <div id={`${id}-min-instantiation-container`} className='tw-relative tw-w-[20%]'>
                    <TextField
                        id={`${id}-min-instantiation-input`}
                        data-testid={`${id}-min-instantiation-input`}
                        label={MIN}
                        type="number"
                        minValue={0}
                        error={!!instanceRangeError}
                        value={String(submodelInfo?.data?.minInstantiation ?? 1)}
                        onChange={(value) => handleMinInstancesChange(Number(value))}
                        onBlur={handleMinInstancesBlur}
                        onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                        placeholder={ENTER}
                    />
                    {showConfigureLockedMinTooltip && (
                        <div className="tw-absolute tw-z-50 tw-bottom-full tw-left-0 tw-mb-1" role="tooltip">
                            <div className="tw-px-2 tw-py-1 tw-bg-tooltip-surface tw-shadow-ds-tooltip tw-rounded-ctl tw-flex tw-items-start tw-w-max tw-max-w-[300px]">
                                <span className="tw-min-w-0 tw-text-tooltip-ink tw-text-caption-metadata tw-break-words tw-text-left">
                                    Setting more than 1 instance is available with Configure
                                </span>
                            </div>
                        </div>
                    )}
                    {showFreestyleMinInstTooltip && (
                        <div className="tw-absolute tw-z-50 tw-bottom-full tw-left-0 tw-mb-1" role="tooltip">
                            <div className="tw-px-2 tw-py-1 tw-bg-tooltip-surface tw-shadow-ds-tooltip tw-rounded-ctl tw-flex tw-items-start tw-w-max tw-max-w-[300px]">
                                <span className="tw-min-w-0 tw-text-tooltip-ink tw-text-caption-metadata tw-break-words tw-text-left">
                                    Freestyle UI modelling supports only a single instance of a submodel. Reduce instances to 1 or switch to a different UI modelling option.
                                </span>
                            </div>
                        </div>
                    )}
                </div>
                <div id={`${id}-max-instantiation-container`} className='tw-relative tw-w-[20%]'>
                    <TextField
                        id={`${id}-max-instantiation-input`}
                        data-testid={`${id}-max-instantiation-input`}
                        label={MAX}
                        type="number"
                        minValue={0}
                        error={!!instanceRangeError}
                        value={String(submodelInfo?.data?.maxInstantiation ?? 1)}
                        onChange={(value) => handleMaxInstancesChange(Number(value))}
                        onBlur={handleMaxInstancesBlur}
                        onKeyDown={(e) => { if (e.key === '-') e.preventDefault(); }}
                        placeholder={ENTER}
                    />
                    {showConfigureLockedMaxTooltip && (
                        <div className="tw-absolute tw-z-50 tw-bottom-full tw-left-0 tw-mb-1" role="tooltip">
                            <div className="tw-px-2 tw-py-1 tw-bg-tooltip-surface tw-shadow-ds-tooltip tw-rounded-ctl tw-flex tw-items-start tw-w-max tw-max-w-[300px]">
                                <span className="tw-min-w-0 tw-text-tooltip-ink tw-text-caption-metadata tw-break-words tw-text-left">
                                    Setting more than 1 instance is available with Configure
                                </span>
                            </div>
                        </div>
                    )}
                    {showFreestyleMaxInstTooltip && (
                        <div className="tw-absolute tw-z-50 tw-bottom-full tw-left-0 tw-mb-1" role="tooltip">
                            <div className="tw-px-2 tw-py-1 tw-bg-tooltip-surface tw-shadow-ds-tooltip tw-rounded-ctl tw-flex tw-items-start tw-w-max tw-max-w-[300px]">
                                <span className="tw-min-w-0 tw-text-tooltip-ink tw-text-caption-metadata tw-break-words tw-text-left">
                                    Freestyle UI modelling supports only a single instance of a submodel. Reduce instances to 1 or switch to a different UI modelling option.
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {instanceRangeError && (
                <Typography
                    variant="caption-metadata"
                    as="p"
                    data-testid={`${id}-instantiation-error`}
                    className='tw-text-tone-danger-ink tw-mt-1'
                >
                    {instanceRangeError}
                </Typography>
            )}

            {/* Class */}
            <div id={`${id}-class-header`} className='tw-flex tw-items-center tw-gap-1 tw-mt-4'>
                <ClassIcon />
                <FieldTitle label={CLASS} data-testid={`${id}-class-title`} />
            </div>
            <div id={`${id}-class-section`} className='tw-flex tw-items-center tw-gap-4 tw-mt-1 tw-pb-3'>
                <div id={`${id}-class-select-container`} className='tw-w-[30%]'>
                    {/* One Tooltip wrapper for both states — `disabled` turns it off
                        outside guide mode, rather than branching the whole control. The
                        wrapper span is what receives the hover, which is the only reason
                        this works over a DISABLED control: a disabled button or select
                        fires no mouse events of its own. */}
                    <Tooltip content={GUIDE_LOCKED_MESSAGE} disabled={!isConfigureDisabled} placement="top" block>
                        {classDropdown}
                    </Tooltip>
                </div>
                <Tooltip content={GUIDE_LOCKED_MESSAGE} disabled={!isConfigureDisabled} placement="top">
                    {manageClassButton}
                </Tooltip>
            </div>

            {showClassMenu ?
                <ManageClassDialog
                    id={`${id}-class-dialog`}
                    showClassMenu={showClassMenu}
                    setShowClassMenu={setShowClassMenu}
                    projectId={projectId}
                />
                : null}
        </Card>
    );
}

export default SubModelDetails;